/**
 * DOS518 - Digital Operations System for Wildfire Management
 *
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 */
// src/components/Risorse/PersonaleTab.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { SelectModal } from '../common/SelectModal';
import { MicInput } from '../common/MicInput';
import { Qualifica } from '../../types';
import {
  ContattoPersonale,
  loadRubricaPersonale,
  aggiungiARubrica,
  rimuoviDaRubrica,
} from '../../data/rubricaPersonale';
import { AstutoImport } from './AstutoImport';

const QUALIFICHE = [
  'Capo Squadra',
  'Autista 1° Grado',
  'Autista 2° Grado',
  'Autista 3° Grado',
  'Autista 4° Grado',
  'Operatore',
  'Volontario',
];

const QUALIFICA_MAP: Record<string, Qualifica> = {
  'Capo Squadra': 'capo_squadra',
  'Autista 1° Grado': 'autista_1_grado',
  'Autista 2° Grado': 'autista_2_grado',
  'Autista 3° Grado': 'autista_3_grado',
  'Autista 4° Grado': 'autista_4_grado',
  'Operatore': 'operatore',
  'Volontario': 'volontario',
};

const QUALIFICA_DISPLAY: Record<Qualifica, string> = {
  capo_squadra: 'Capo Squadra',
  autista_1_grado: 'Autista 1° Grado',
  autista_2_grado: 'Autista 2° Grado',
  autista_3_grado: 'Autista 3° Grado',
  autista_4_grado: 'Autista 4° Grado',
  operatore: 'Operatore',
  volontario: 'Volontario',
};

const FORM_VUOTO = { nome: '', cognome: '', telefono: '', radio: '', qualifica: '' };

export const PersonaleTab: React.FC = () => {
  const personale = useAppStore((state) => state.personale);
  const mezziTerrestri = useAppStore((state) => state.mezziTerrestri);
  const addPersonale = useAppStore((state) => state.addPersonale);
  const updatePersonale = useAppStore((state) => state.updatePersonale);
  const deletePersonale = useAppStore((state) => state.deletePersonale);

  // Form
  const [formData, setFormData] = useState(FORM_VUOTO);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showQualificaModal, setShowQualificaModal] = useState(false);

  // Rubrica personale (localStorage)
  const [rubrica, setRubrica] = useState<ContattoPersonale[]>(loadRubricaPersonale);
  const [showRubrica, setShowRubrica] = useState(false);

  // ASTUTO import
  const [showAstuto, setShowAstuto] = useState(false);

  // Autocomplete
  const [suggerimenti, setSuggerimenti] = useState<ContattoPersonale[]>([]);
  const [showSuggerimenti, setShowSuggerimenti] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  // Chiudi dropdown cliccando fuori
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target as Node)) {
        setShowSuggerimenti(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Filtra rubrica mentre si digita
  const aggiornaAutocomplete = (nome: string, cognome: string) => {
    const query = `${nome} ${cognome}`.trim().toLowerCase();
    if (query.length < 1) {
      setSuggerimenti([]);
      setShowSuggerimenti(false);
      return;
    }
    const filtrati = rubrica.filter((c) => {
      const full = `${c.nome} ${c.cognome}`.trim().toLowerCase();
      return (
        c.nome.toLowerCase().startsWith(nome.toLowerCase()) ||
        c.cognome.toLowerCase().startsWith(cognome.toLowerCase()) ||
        full.includes(query)
      );
    });
    setSuggerimenti(filtrati);
    setShowSuggerimenti(filtrati.length > 0);
  };

  const handleNomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, nome: val }));
    aggiornaAutocomplete(val, formData.cognome);
  };

  const handleCognomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, cognome: val }));
    aggiornaAutocomplete(formData.nome, val);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Selezione suggerimento → compilazione automatica
  const selezionaSuggerimento = (c: ContattoPersonale) => {
    setFormData({
      nome: c.nome,
      cognome: c.cognome,
      telefono: c.telefono,
      radio: c.radio || '',
      qualifica: QUALIFICA_DISPLAY[c.qualifica] || '',
    });
    setShowSuggerimenti(false);
    setSuggerimenti([]);
  };

  // Verifica se il form corrisponde già a un contatto in rubrica
  const giaInRubrica = (): boolean => {
    const chiave = `${formData.nome} ${formData.cognome}`.trim().toLowerCase();
    return rubrica.some(
      (c) => `${c.nome} ${c.cognome}`.trim().toLowerCase() === chiave
    );
  };

  const handleSalvaInRubrica = () => {
    if (!formData.nome || !formData.qualifica) return;
    const qualificaValue = QUALIFICA_MAP[formData.qualifica];
    if (!qualificaValue) return;
    const nuova = aggiungiARubrica(rubrica, {
      nome: formData.nome,
      cognome: formData.cognome,
      telefono: formData.telefono,
      radio: formData.radio || undefined,
      qualifica: qualificaValue,
    });
    setRubrica(nuova);
  };

  const handleRimuoviDaRubrica = (id: string) => {
    if (!confirm('Rimuovere questo contatto dalla rubrica?')) return;
    setRubrica(rimuoviDaRubrica(rubrica, id));
  };

  const handleSubmit = () => {
    if (!formData.nome || !formData.telefono || !formData.qualifica) {
      alert('⚠️ Nome, Telefono e Qualifica sono obbligatori');
      return;
    }

    const normalizza = (n: string, c: string) =>
      `${n} ${c}`.trim().toLowerCase().replace(/\s+/g, ' ');

    const nomeNuovo = normalizza(formData.nome, formData.cognome);
    const duplicato = personale.find((p) => {
      if (p.id === editingId) return false;
      return normalizza(p.nome, p.cognome) === nomeNuovo;
    });

    if (duplicato) {
      alert(
        `⚠️ Esiste già un operatore con nome "${formData.nome} ${formData.cognome}".`
      );
      return;
    }

    const qualificaValue = QUALIFICA_MAP[formData.qualifica];
    if (editingId) {
      updatePersonale(editingId, { ...formData, qualifica: qualificaValue } as any);
      setEditingId(null);
    } else {
      addPersonale({ ...formData, qualifica: qualificaValue } as any);
    }

    resetForm();
  };

  const resetForm = () => {
    setFormData(FORM_VUOTO);
    setEditingId(null);
    setSuggerimenti([]);
    setShowSuggerimenti(false);
  };

  const handleEdit = (persona: any) => {
    setFormData({
      nome: persona.nome,
      cognome: persona.cognome,
      telefono: persona.telefono,
      radio: persona.radio || '',
      qualifica: QUALIFICA_DISPLAY[persona.qualifica] || '',
    });
    setEditingId(persona.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id: string) => {
    if (confirm('Sei sicuro di voler eliminare questo operatore?')) {
      deletePersonale(id);
    }
  };

  const getMezzoAssegnato = (nomeCompleto: string): string => {
    const mezzo = mezziTerrestri.find(
      (m) => m.autista === nomeCompleto || m.capoSquadra === nomeCompleto
    );
    return mezzo ? mezzo.sigla : 'N/D';
  };

  const formCompilato =
    formData.nome.trim() !== '' && formData.qualifica !== '';

  return (
    <div className="risorse-tab-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0 }}>👥 Gestione Personale</h3>
        <button
          style={styles.btnAstuto}
          onClick={() => setShowAstuto(true)}
          title="Importa personale e mezzi da ASTUTO (CFRS)"
        >
          🌲 Importa da ASTUTO
        </button>
      </div>

      {/* ── FORM INSERIMENTO ── */}
      <div className="form-container">
        <h4>{editingId ? '✏️ Modifica Operatore' : '➕ Nuovo Operatore'}</h4>

        {/* Nome con autocomplete */}
        <div className="form-row" ref={autocompleteRef} style={{ position: 'relative' }}>
          <div className="form-group">
            <label>Nome *</label>
            <MicInput
              type="text"
              name="nome"
              value={formData.nome}
              appendMode={false}
              onChange={handleNomeChange}
              onFocus={() => {
                if (suggerimenti.length > 0) setShowSuggerimenti(true);
              }}
              placeholder="Nome (digita o ditta vocalmente)"
              autoComplete="off"
            />
          </div>
          <div className="form-group">
            <label>Cognome</label>
            <MicInput
              type="text"
              name="cognome"
              value={formData.cognome}
              appendMode={false}
              onChange={handleCognomeChange}
              placeholder="Cognome (digita o ditta vocalmente)"
              autoComplete="off"
            />
          </div>

          {/* Dropdown suggerimenti */}
          {showSuggerimenti && (
            <div style={styles.dropdown}>
              <div style={styles.dropdownHeader}>
                📋 Rubrica — seleziona per compilare automaticamente
              </div>
              {suggerimenti.map((c) => (
                <div
                  key={c.id}
                  className="autocomplete-dropdown-row"
                  style={styles.dropdownRow}
                  onMouseDown={() => selezionaSuggerimento(c)}
                >
                  <div style={styles.dropdownNome}>
                    👤 {c.nome} {c.cognome}
                  </div>
                  <div style={styles.dropdownInfo}>
                    <span style={styles.badge}>{QUALIFICA_DISPLAY[c.qualifica]}</span>
                    {c.ente && <span style={styles.ente}>{c.ente}</span>}
                    <span style={styles.tel}>📞 {c.telefono}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Telefono *</label>
            <input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleInputChange}
              placeholder="+39 123 456 7890"
            />
          </div>
          <div className="form-group">
            <label>Radio</label>
            <input
              type="text"
              name="radio"
              value={formData.radio}
              onChange={handleInputChange}
              placeholder="Frequenza radio"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Qualifica *</label>
          <input
            type="text"
            value={formData.qualifica}
            placeholder="Seleziona qualifica..."
            readOnly
            onClick={() => setShowQualificaModal(true)}
            style={{ cursor: 'pointer' }}
          />
        </div>

        <div className="form-actions">
          <button className="btn btn-success" onClick={handleSubmit}>
            {editingId ? '✏️ Salva Modifiche' : '💾 Salva Operatore'}
          </button>
          {editingId && (
            <button
              className="btn btn-secondary"
              onClick={resetForm}
            >
              ✕ Annulla
            </button>
          )}
          {/* Salva in rubrica — visibile solo se il form è compilato e il contatto non è già presente */}
          {formCompilato && !editingId && !giaInRubrica() && (
            <button
              className="btn"
              style={styles.btnRubrica}
              onClick={handleSalvaInRubrica}
              title="Salva questo operatore nella rubrica per un uso futuro"
            >
              📋 Salva in rubrica
            </button>
          )}
          {formCompilato && !editingId && giaInRubrica() && (
            <span style={styles.giaInRubricaLabel}>✓ In rubrica</span>
          )}
        </div>
      </div>

      {/* ── LISTA PERSONALE INTERVENTO ── */}
      <div className="lista-header">
        <h4>Lista Personale ({personale.length})</h4>
      </div>

      <div className="risorse-list">
        {personale.length === 0 ? (
          <div className="empty-state">
            <p>👤 Nessun operatore inserito</p>
          </div>
        ) : (
          personale.map((persona) => {
            const nomeCompleto = `${persona.nome} ${persona.cognome}`.trim();
            const mezzoAssegnato = getMezzoAssegnato(nomeCompleto);
            return (
              <div key={persona.id} className="risorsa-card">
                <div className="card-header-risorsa">
                  <span className="icon">👤</span>
                  <h4>
                    {nomeCompleto}{' '}
                    <span className={`badge badge-qualifica-${persona.qualifica}`}>
                      {QUALIFICA_DISPLAY[persona.qualifica]}
                    </span>
                  </h4>
                </div>
                <div className="card-body-risorsa">
                  <p><strong>Radio:</strong> {persona.radio || 'N/D'}</p>
                  <p><strong>Tel:</strong> {persona.telefono}</p>
                  <p><strong>Mezzo:</strong> {mezzoAssegnato}</p>
                </div>
                <div className="card-actions">
                  <button className="btn-edit" onClick={() => handleEdit(persona)}>✏️ Modifica</button>
                  <button className="btn-delete" onClick={() => handleDelete(persona.id)}>🗑️ Elimina</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── RUBRICA PERSONALE ── */}
      <div style={styles.rubricaSection}>
        <button
          style={styles.rubricaToggle}
          onClick={() => setShowRubrica((v) => !v)}
        >
          <span>📋 Rubrica Personale ({rubrica.length})</span>
          <span>{showRubrica ? '▲' : '▼'}</span>
        </button>

        {showRubrica && (
          <div style={styles.rubricaBody}>
            {rubrica.length === 0 ? (
              <div style={styles.rubricaVuota}>
                Nessun contatto in rubrica.<br />
                <small>Compila il form e clicca "Salva in rubrica" per aggiungere operatori.</small>
              </div>
            ) : (
              rubrica.map((c) => (
                <div key={c.id} style={styles.rubricaRiga}>
                  <div style={styles.rubricaInfo}>
                    <span style={styles.rubricaNome}>👤 {c.nome} {c.cognome}</span>
                    <span style={styles.badge}>{QUALIFICA_DISPLAY[c.qualifica]}</span>
                    {c.ente && <span style={styles.ente}>{c.ente}</span>}
                  </div>
                  <div style={styles.rubricaSub}>
                    <span>📞 {c.telefono}</span>
                    {c.radio && <span>📻 {c.radio}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      style={styles.btnUsa}
                      onClick={() => selezionaSuggerimento(c)}
                      title="Usa nel form"
                    >
                      ↑ Usa
                    </button>
                    <button
                      style={styles.btnElimina}
                      onClick={() => handleRimuoviDaRubrica(c.id)}
                      title="Rimuovi dalla rubrica"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <SelectModal
        isOpen={showQualificaModal}
        onClose={() => setShowQualificaModal(false)}
        title="Seleziona Qualifica"
        options={QUALIFICHE}
        selectedValue={formData.qualifica}
        onSelect={(value) => setFormData({ ...formData, qualifica: value })}
      />

      {showAstuto && (
        <AstutoImport onClose={() => setShowAstuto(false)} />
      )}
    </div>
  );
};

// ─── Stili inline per i nuovi elementi ────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  btnAstuto: {
    padding: '9px 16px',
    borderRadius: 7, border: 'none',
    background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
    color: '#fff', fontWeight: 700, fontSize: 13,
    cursor: 'pointer',
    boxShadow: '0 4px 10px rgba(37,99,235,0.35)',
    flexShrink: 0,
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    background: '#1a1f2e',
    border: '1px solid #ff6b35',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    maxHeight: 260,
    overflowY: 'auto',
  },
  dropdownHeader: {
    padding: '8px 12px',
    fontSize: 11,
    color: '#ff6b35',
    borderBottom: '1px solid #2a2f3e',
    fontWeight: 600,
    letterSpacing: 0.5,
  },
  dropdownRow: {
    padding: '10px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #1e2535',
    transition: 'background 0.15s',
  },
  dropdownNome: {
    fontWeight: 600,
    color: '#e2e8f0',
    fontSize: 14,
  },
  dropdownInfo: {
    display: 'flex',
    gap: 8,
    marginTop: 3,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  badge: {
    background: 'rgba(255,107,53,0.2)',
    color: '#ff6b35',
    padding: '2px 7px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
  },
  ente: {
    color: '#94a3b8',
    fontSize: 11,
  },
  tel: {
    color: '#64748b',
    fontSize: 11,
  },
  btnRubrica: {
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid #6366f1',
    color: '#a5b4fc',
    padding: '8px 14px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  },
  giaInRubricaLabel: {
    color: '#4ade80',
    fontSize: 12,
    fontWeight: 600,
    padding: '8px 10px',
  },
  rubricaSection: {
    marginTop: 28,
    border: '1px solid rgba(255,107,53,0.3)',
    borderRadius: 10,
    overflow: 'hidden',
  },
  rubricaToggle: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    background: 'rgba(255,107,53,0.08)',
    border: 'none',
    color: '#ff6b35',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: 0.3,
  },
  rubricaBody: {
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    background: 'rgba(255,255,255,0.03)',
  },
  rubricaVuota: {
    color: '#64748b',
    fontSize: 13,
    textAlign: 'center',
    padding: '16px 0',
    lineHeight: 1.6,
  },
  rubricaRiga: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 7,
    border: '1px solid rgba(255,107,53,0.15)',
    flexWrap: 'wrap',
    gap: 6,
  },
  rubricaInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    flex: 1,
  },
  rubricaNome: {
    fontWeight: 600,
    color: '#e2e8f0',
    fontSize: 14,
  },
  rubricaSub: {
    display: 'flex',
    gap: 10,
    color: '#64748b',
    fontSize: 12,
  },
  btnUsa: {
    background: 'rgba(255,107,53,0.15)',
    border: '1px solid rgba(255,107,53,0.4)',
    color: '#ff6b35',
    padding: '4px 10px',
    borderRadius: 5,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
  },
  btnElimina: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#f87171',
    padding: '4px 8px',
    borderRadius: 5,
    cursor: 'pointer',
    fontSize: 12,
  },
};
